import React, { useCallback, useEffect, useState } from "react";
import { api, DeletedPlayerRewardEntry, PlayerRewardEntry, Reward } from "../api/client";
import { useConfirm } from "../components/ConfirmDialog";
import PageLoader from "../components/PageLoader";

const MONTHS = [
  "январь",
  "февраль",
  "март",
  "апрель",
  "май",
  "июнь",
  "июль",
  "август",
  "сентябрь",
  "октябрь",
  "ноябрь",
  "декабрь",
];

// "2026-04-01" → "апрель 2026". Parsed from the string, not via Date:
// new Date("2026-04-01") is UTC midnight and lands in March west of Greenwich.
function formatMonth(month: string): string {
  const [year, mm] = month.split("-");
  const name = MONTHS[Number(mm) - 1];
  return name ? `${name} ${year}` : month;
}

function formatDeletedAt(iso: string): string {
  return new Date(iso).toLocaleString("ru-RU", {
    day: "numeric",
    month: "long",
    hour: "2-digit",
    minute: "2-digit",
  });
}

export default function PlayerRewardsPage() {
  const [entries, setEntries] = useState<PlayerRewardEntry[]>([]);
  const [deleted, setDeleted] = useState<DeletedPlayerRewardEntry[]>([]);
  const [rewards, setRewards] = useState<Reward[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [busyId, setBusyId] = useState<string | null>(null);
  const [picked, setPicked] = useState<Record<string, string>>({});
  const { confirm, dialog } = useConfirm();

  const load = useCallback(async () => {
    try {
      const [queue, catalog, removed] = await Promise.all([
        api.listAwaitingPlayerRewards(),
        api.listRewards(),
        // The admin panel and the server are deployed separately: on a server
        // without deletion the queue must still work, just without this list.
        api.listDeletedPlayerRewards().catch((err) => {
          console.error("Failed to load deleted player rewards", err);
          return null;
        }),
      ]);
      setEntries(queue ?? []);
      setRewards(catalog ?? []);
      setDeleted(removed ?? []);
    } catch (err: any) {
      console.error("Failed to load player rewards", err);
      setError(err?.message || "Не удалось загрузить награды");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const inStock = rewards.filter((r) => r.count > 0);
  const busy = busyId !== null;

  const run = async (id: string, action: () => Promise<unknown>, failure: string) => {
    setBusyId(id);
    setError(null);
    try {
      await action();
    } catch (err: any) {
      console.error("Player reward action failed", err);
      setError(err?.message || failure);
    } finally {
      setBusyId(null);
      await load();
    }
  };

  const describe = (entry: { player_name: string; position: number; month: string }) =>
    `${entry.player_name}: ${entry.position} место, ${formatMonth(entry.month)}.`;

  const handOver = async (entry: PlayerRewardEntry, actionLabel: string) => {
    const rewardID = entry.status === "pending" ? picked[entry.id] : undefined;
    const prize =
      entry.status === "selected"
        ? entry.reward_title
        : rewards.find((r) => r.id === rewardID)?.title;
    const confirmed = await confirm({
      title: "Отметить награду выданной?",
      body: (
        <>
          <p>{describe(entry)}</p>
          <p>Приз: {prize ?? "без приза"}</p>
        </>
      ),
      confirmLabel: actionLabel,
    });
    if (!confirmed) return;

    await run(
      entry.id,
      async () => {
        // Select first: it attaches the prize and takes it off the stock. If it
        // fails, the reward must not be closed out without the prize.
        if (rewardID) await api.selectPlayerReward(entry.id, rewardID);
        await api.markPlayerRewardGiven(entry.id);
      },
      "Не удалось отметить выдачу"
    );
  };

  const remove = async (entry: PlayerRewardEntry) => {
    const confirmed = await confirm({
      title: "Удалить награду?",
      body: (
        <>
          <p>{describe(entry)}</p>
          <p>
            Награда пропадёт из очереди и из приложения игрока.
            {entry.status === "selected" &&
              ` Выбранный приз «${entry.reward_title ?? "—"}» вернётся на склад.`}
          </p>
          <p>Восстановить её можно ниже, в разделе «Удалённые награды».</p>
        </>
      ),
      confirmLabel: "Удалить",
      danger: true,
    });
    if (!confirmed) return;

    await run(entry.id, () => api.deletePlayerReward(entry.id), "Не удалось удалить награду");
  };

  const restore = (entry: DeletedPlayerRewardEntry) =>
    run(entry.id, () => api.restorePlayerReward(entry.id), "Не удалось восстановить награду");

  if (loading) return <PageLoader />;

  return (
    <div className="page page-wide">
      <h2>Награды игроков</h2>
      <p className="page-description">
        Очередь выдачи призов победителям месяца. Если игрок ещё не выбрал приз,
        выберите тот, что отдали, — он спишется со склада. Когда призов на складе
        нет, награду можно отметить выданной без приза. Лишнюю награду можно
        удалить и при необходимости восстановить.
      </p>

      {error && (
        <div className="error" role="alert">
          {error}
        </div>
      )}

      <div className="table-wrap">
        <table className="settings-table stack-table reward-queue">
          <thead>
            <tr>
              <th>Игрок</th>
              <th>Группа</th>
              <th>Месяц</th>
              <th>Место</th>
              <th>Статус</th>
              <th>Приз</th>
              <th />
            </tr>
          </thead>
          <tbody>
            {entries.length === 0 ? (
              <tr>
                <td colSpan={7} className="empty-cell">
                  {error ? "—" : "Все награды выданы"}
                </td>
              </tr>
            ) : (
              entries.map((e) => (
                <tr key={e.id}>
                  <td className="cell-player">{e.player_name}</td>
                  <td className="cell-group">{e.group_name}</td>
                  <td className="date-cell cell-month">{formatMonth(e.month)}</td>
                  <td className="cell-place">
                    <span className={`place place-${e.position}`}>{e.position}</span>
                  </td>
                  <td className="cell-status">
                    {e.status === "selected" ? (
                      <span className="mav-badge mav-badge-active">Выбран игроком</span>
                    ) : (
                      <span className="mav-badge mav-badge-muted">Ждёт выбора</span>
                    )}
                  </td>
                  <td className="cell-prize">
                    {e.status === "selected" ? (
                      e.reward_title ?? "—"
                    ) : inStock.length > 0 ? (
                      <select
                        className="mav-input"
                        aria-label={`Приз для ${e.player_name}`}
                        value={picked[e.id] ?? ""}
                        disabled={busy}
                        onChange={(ev) =>
                          setPicked((p) => ({ ...p, [e.id]: ev.target.value }))
                        }
                      >
                        <option value="">Выберите приз</option>
                        {inStock.map((r) => (
                          <option key={r.id} value={r.id}>
                            {r.title} ({r.count} шт.)
                          </option>
                        ))}
                      </select>
                    ) : (
                      <span className="mav-status-muted">Нет на складе</span>
                    )}
                  </td>
                  <td className="cell-action">
                    <div className="row-actions">
                      {e.status === "selected" ? (
                        <button
                          className="mav-btn-primary"
                          disabled={busy}
                          onClick={() => handOver(e, "Выдано")}
                        >
                          Выдано
                        </button>
                      ) : inStock.length > 0 ? (
                        <button
                          className="mav-btn-primary"
                          disabled={busy || !picked[e.id]}
                          onClick={() => handOver(e, "Выдать")}
                        >
                          Выдать
                        </button>
                      ) : (
                        <button
                          className="mav-btn-secondary"
                          disabled={busy}
                          onClick={() => handOver(e, "Выдать без приза")}
                        >
                          Выдать без приза
                        </button>
                      )}
                      <button
                        className="btn-quiet-danger"
                        disabled={busy}
                        onClick={() => remove(e)}
                      >
                        Удалить
                      </button>
                    </div>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {deleted.length > 0 && (
        <section className="deleted-rewards" aria-labelledby="deleted-rewards-title">
          <h3 id="deleted-rewards-title">Удалённые награды</h3>
          <p className="page-description">
            Их не видно в очереди и в приложении игрока. Восстановленная награда
            вернётся в очередь со статусом «Ждёт выбора».
          </p>
          <div className="table-wrap">
            <table className="settings-table stack-table reward-deleted">
              <thead>
                <tr>
                  <th>Игрок</th>
                  <th>Группа</th>
                  <th>Месяц</th>
                  <th>Место</th>
                  <th>Удалена</th>
                  <th />
                </tr>
              </thead>
              <tbody>
                {deleted.map((d) => (
                  <tr key={d.id}>
                    <td className="cell-player">{d.player_name}</td>
                    <td className="cell-group">{d.group_name}</td>
                    <td className="date-cell cell-month">{formatMonth(d.month)}</td>
                    <td className="cell-place">
                      <span className={`place place-${d.position}`}>{d.position}</span>
                    </td>
                    <td className="date-cell cell-deleted" data-label="Удалена">
                      {formatDeletedAt(d.deleted_at)}
                    </td>
                    <td className="cell-action">
                      <button
                        className="mav-btn-secondary"
                        disabled={busy}
                        onClick={() => restore(d)}
                      >
                        Восстановить
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </section>
      )}

      {dialog}
    </div>
  );
}
